import React from "react";
import { twMerge } from "tailwind-merge";

interface StyledTextProps {
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
}

const EscalatorText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[length:var(--text-body)] leading-[var(--text-body--line-height)] font-medium tracking-[0.25px] text-foreground-muted select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const BodyTitleText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-heading text-[16px] font-medium leading-[24px] tracking-[0.08px] text-foreground select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const HeadlineText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-heading text-[length:var(--text-headline)] leading-[var(--text-headline--line-height)] font-[number:var(--font-weight-headline)] tracking-[0.1px] text-foreground select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const HeadlineBodyText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-heading text-[length:var(--text-headline-body)] leading-[var(--text-headline-body--line-height)] font-[number:var(--font-weight-headline-body)] tracking-[0.2px] text-foreground select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const BodyText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[length:var(--text-body)] leading-[var(--text-body--line-height)] tracking-[0.25px] text-foreground-muted select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Brand-aware: weight, tracking, and case come from --brand-button-* CSS
// variables (see packages/config-tailwind/brands/{kinsa,owlet}.css). Kinsa
// renders uppercase with 1.5px tracking at weight 500; Owlet renders sentence
// case with 0 tracking at weight 600. Size and line-height already adapt
// per-brand via --text-body / --text-body--line-height.
const ButtonText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[length:var(--text-body)] leading-[var(--text-body--line-height)] [font-weight:var(--button-font-weight)] [letter-spacing:var(--button-tracking)] [text-transform:var(--button-text-transform)] select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const SublineCondensedText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[12px] font-regular leading-[16px] tracking-[0.25px] select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const SublineText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[12px] font-medium leading-[18px] tracking-[1.5px] text-accent select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const SubheadlineText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "span",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[15px] font-normal leading-snug tracking-normal text-foreground-muted select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const SectionLabelText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[15px] uppercase font-normal leading-snug tracking-tight text-foreground select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const DisclaimerText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[12px] font-medium leading-[18px] tracking-wider text-foreground-muted select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

const ChatText: React.FC<StyledTextProps> = ({
  children,
  className,
  as: Component = "p",
  ...props
}) => {
  return (
    <Component
      className={twMerge(
        "font-sans text-[17px] font-normal leading-[22px] tracking-[0.25px] text-foreground-muted select-none",
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

export {
  HeadlineText,
  HeadlineBodyText,
  BodyText,
  ButtonText,
  SublineText,
  SubheadlineText,
  SectionLabelText,
  DisclaimerText,
  BodyTitleText,
  EscalatorText,
  SublineCondensedText,
  ChatText,
};
